/**
 * Remote terminal channel.
 *
 * Like file transfer, the terminal is its own connection type: a session
 * opened with connType "terminal" carries shells, not the screen. The peer
 * starts its terminal service on login; we then open a terminal, stream
 * keystrokes to it, and receive its output.
 *
 * Output is raw PTY bytes including ANSI escapes. This layer stays byte-exact
 * and leaves interpretation to the view.
 */
import { decompress } from "fzstd";
import { encodeTerminalOpen, encodeTerminalData, encodeTerminalResize, encodeTerminalClose, } from "./message.js";
export class RemoteTerminal {
    #session;
    #events;
    #open = new Set();
    #decoder = new TextDecoder();
    constructor(session, events = {}) {
        this.#session = session;
        this.#events = events;
    }
    /** Feed every session message here. */
    handle(msg) {
        switch (msg.kind) {
            case "terminal_opened":
                if (msg.success) {
                    this.#open.add(msg.terminalId);
                    this.#events.onOpened?.({
                        terminalId: msg.terminalId, pid: msg.pid, serviceId: msg.serviceId,
                    });
                }
                else {
                    this.#events.onError?.(msg.message || "The remote terminal did not start");
                }
                return;
            case "terminal_data": {
                // The peer compresses whenever that comes out smaller, so anything
                // beyond a few characters arrives compressed - which is to say all
                // real output. It is not negotiated and cannot be turned off.
                let data = msg.data;
                if (msg.compressed) {
                    try {
                        data = decompress(data);
                    }
                    catch {
                        this.#events.onError?.("A block of terminal output could not be read");
                        return;
                    }
                }
                this.#events.onOutput?.(msg.terminalId, data);
                return;
            }
            case "terminal_closed":
                this.#open.delete(msg.terminalId);
                this.#events.onClosed?.(msg.terminalId, msg.exitCode);
                return;
            case "terminal_error":
                this.#events.onError?.(msg.message || "Terminal error");
                return;
            default:
                return;
        }
    }
    open(terminalId = 0, rows = 24, cols = 80) {
        this.#session.sendRaw(encodeTerminalOpen(terminalId, rows, cols));
    }
    /** Sends typed input. */
    write(text, terminalId = 0) {
        this.#session.sendRaw(encodeTerminalData(terminalId, new TextEncoder().encode(text)));
    }
    writeBytes(data, terminalId = 0) {
        this.#session.sendRaw(encodeTerminalData(terminalId, data));
    }
    resize(rows, cols, terminalId = 0) {
        this.#session.sendRaw(encodeTerminalResize(terminalId, rows, cols));
    }
    close(terminalId = 0) {
        this.#open.delete(terminalId);
        this.#session.sendRaw(encodeTerminalClose(terminalId));
    }
    decode(data) {
        // stream:true so a multi-byte character split across packets survives.
        return this.#decoder.decode(data, { stream: true });
    }
}
/**
 * Translates a browser key event into the bytes a PTY expects.
 * Returns null when the key should be ignored.
 */
export function keyToTerminalBytes(e) {
    const ctrl = e.ctrlKey;
    if (ctrl && e.key.length === 1) {
        const c = e.key.toUpperCase().charCodeAt(0);
        // Ctrl-A..Ctrl-Z and the neighbouring control codes.
        if (c >= 64 && c <= 95)
            return String.fromCharCode(c - 64);
    }
    switch (e.key) {
        case "Enter": return "\r";
        case "Backspace": return "\x7f";
        case "Tab": return "\t";
        case "Escape": return "\x1b";
        case "ArrowUp": return "\x1b[A";
        case "ArrowDown": return "\x1b[B";
        case "ArrowRight": return "\x1b[C";
        case "ArrowLeft": return "\x1b[D";
        case "Home": return "\x1b[H";
        case "End": return "\x1b[F";
        case "PageUp": return "\x1b[5~";
        case "PageDown": return "\x1b[6~";
        case "Delete": return "\x1b[3~";
        case "Insert": return "\x1b[2~";
        default: break;
    }
    if (e.key.length === 1 && !e.metaKey) {
        // Alt sends ESC followed by the character.
        return e.altKey ? "\x1b" + e.key : e.key;
    }
    return null;
}
